import {
  QualtricsRetailerEvent,
  triggerQualtricsEvent,
} from "@faire/web--source/common/in-product-survey/qualtrics";
import { trackCategoriesLayoutElementCtaClick } from "@faire/web-api--source/events/categories/click/layoutElementCta";
import { trackCategoriesLayoutElementItemClick } from "@faire/web-api--source/events/categories/click/layoutElementItem";
import { trackHomeFeedLayoutElementCtaClick } from "@faire/web-api--source/events/homeFeed/click/layoutElementCta";
import { trackHomeFeedLayoutElementItemClick } from "@faire/web-api--source/events/homeFeed/click/layoutElementItem";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { useCallback } from "react";

import { logSDUIEvent } from "@faire/retailer/components/SDUI/__internal__/EventLogging/logSDUIEvent";
import {
  ActionLoggingEventParams,
  ComponentLoggingEventParams,
  LoggingEventParams,
} from "@faire/retailer/components/SDUI/__internal__/types";

import { useSDUIActionsHandler } from "./useSDUIActionsHandler";

export function useSDUIOnClickHandler(
  actions: IAction[],
  loggingData: LoggingEventParams,
  eventOptions: {
    logSDUIClickEvent: boolean;
    // TODO: https://fairewholesale.atlassian.net/browse/FD-217518
    // The logLegacyClickEvent option should be removed from the useSDUIOnClickHandler
    // once all layout element surfaces have been migrated over to SDUI
    // and data pipelines and dashboards no longer need the layout element events
    logLegacyClickEvent?: boolean;
  } = {
    logSDUIClickEvent: true,
    logLegacyClickEvent: false,
  }
): () => void {
  const actionsHandler = useSDUIActionsHandler(actions, loggingData);

  return useCallback(() => {
    actionsHandler();

    if (eventOptions.logSDUIClickEvent) {
      logSDUIEvent("CLICK", loggingData);
    }

    if (
      eventOptions.logLegacyClickEvent &&
      "component_name" in loggingData &&
      loggingData.component_name
    ) {
      fireLegacyLayoutClickEvent(loggingData);
    }
  }, [actionsHandler, loggingData, eventOptions]);
}

const fireLegacyLayoutClickEvent = (
  loggingData: ComponentLoggingEventParams | ActionLoggingEventParams
) => {
  if (loggingData.view_display_page === DisplayPageType.HOMEPAGE) {
    if (
      "action_instance_identifier" in loggingData &&
      loggingData.action_instance_identifier
    ) {
      trackHomeFeedLayoutElementItemClick(
        loggingData.component_name ?? "",
        loggingData.request_id ?? "",
        loggingData.action_instance_identifier
      );
    } else {
      trackHomeFeedLayoutElementCtaClick(
        loggingData.component_name ?? "",
        loggingData.request_id ?? ""
      );
    }
  }
  if (loggingData.view_display_page === DisplayPageType.CATEGORY_PAGE) {
    if (
      "action_instance_identifier" in loggingData &&
      loggingData.action_instance_identifier
    ) {
      trackCategoriesLayoutElementItemClick(
        loggingData.component_name ?? "",
        loggingData.request_id ?? "",
        loggingData.action_instance_identifier
      );
    } else {
      trackCategoriesLayoutElementCtaClick(
        loggingData.component_name ?? "",
        loggingData.request_id ?? ""
      );
    }
  }

  // TODO: https://fairewholesale.atlassian.net/browse/FD-251821
  // clean up after Discovery sub-jobs measurement survey is complete
  if (
    loggingData.view_display_page === DisplayPageType.HOMEPAGE &&
    (loggingData.component_type === "PRODUCT_TILE_CAROUSEL" ||
      loggingData.component_type === "SMALL_MAKER_TILE_CAROUSEL")
  ) {
    triggerQualtricsEvent(QualtricsRetailerEvent.clickOnHomepageCarousel);
  }
};
